import { useRef, useEffect } from "react";
import { motion, useAnimation, useInView } from "framer-motion";

export default function AnimatedReveal({
  children,
  animation = "fade-up",
  delay = 0,
  duration = 0.6,
  className = "",
  as = "div", // 'section', 'article', etc.
  onClick,
}) {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { triggerOnce: true, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  const animationVariants = {
    "fade-up": {
      hidden: { opacity: 0, y: 30 },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration,
          delay,
          ease: [0.22, 1, 0.32, 1],
        },
      },
    },
    "fade-down": {
      hidden: { opacity: 0, y: -30 },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration,
          delay,
          ease: [0.22, 1, 0.34, 1],
        },
      },
    },
    "fade-left": {
      hidden: { opacity: 0, x: -30 },
      visible: {
        opacity: 1,
        x: 0,
        transition: {
          duration,
          delay,
          ease: [0.22, 1, 0.34, 1],
        },
      },
    },
    "fade-right": {
      hidden: { opacity: 0, x: 30 },
      visible: {
        opacity: 1,
        x: 0,
        transition: {
          duration,
          delay,
          ease: [0.22, 1, 0.34, 1],
        },
      },
    },
    "scale-in": {
      hidden: { opacity: 0, scale: 0.9 },
      visible: {
        opacity: 1,
        scale: 1,
        transition: {
          duration,
          delay,
          ease: [0.22, 1, 0.34, 1],
        },
      },
    },
    "zoom-in": {
      hidden: { opacity: 0, scale: 0.8 },
      visible: {
        opacity: 1,
        scale: 1,
        transition: {
          duration,
          delay,
          ease: [0.22, 1, 0.34, 1],
        },
      },
    },
    "slide-up": {
      hidden: {
        opacity: 0,
        y: 60,
        scale: 0.95,
      },
      visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
          duration,
          delay,
          ease: [0.22, 1, 0.34, 1],
        },
      },
    },
    "slide-down": {
      hidden: {
        opacity: 0,
        y: -60,
        scale: 0.95,
      },
      visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
          duration,
          delay,
          ease: [0.22, 1, 0.34, 1],
        },
      },
    },
    "slide-left": {
      hidden: {
        opacity: 0,
        x: -60,
        scale: 0.95,
      },
      visible: {
        opacity: 1,
        x: 0,
        scale: 1,
        transition: {
          duration,
          delay,
          ease: [0.22, 1, 0.34, 1],
        },
      },
    },
    "slide-right": {
      hidden: {
        opacity: 0,
        x: 60,
        scale: 0.95,
      },
      visible: {
        opacity: 1,
        x: 0,
        scale: 1,
        transition: {
          duration,
          delay,
          ease: [0.22, 1, 0.34, 1],
        },
      },
    },
    "flip-up": {
      hidden: {
        opacity: 0,
        rotateX: 90,
        y: 30,
      },
      visible: {
        opacity: 1,
        rotateX: 0,
        y: 0,
        transition: {
          duration,
          delay,
          ease: [0.22, 1, 0.34, 1],
        },
      },
    },
    "flip-down": {
      hidden: {
        opacity: 0,
        rotateX: -90,
        y: -30,
      },
      visible: {
        opacity: 1,
        rotateX: 0,
        y: 0,
        transition: {
          duration,
          delay,
          ease: [0.22, 1, 0.34, 1],
        },
      },
    },
    "bounce-in": {
      hidden: {
        opacity: 0,
        scale: 0.8,
        y: 30,
      },
      visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
          duration,
          delay,
          ease: [0.22, 1, 0.34, 1],
        },
      },
    },
    "rotate-in": {
      hidden: {
        opacity: 0,
        rotate: -90,
      },
      visible: {
        opacity: 1,
        rotate: 0,
        transition: {
          duration,
          delay,
          ease: [0.22, 1, 0.32, 1],
        },
      },
    },

    "skew-in": {
      hidden: {
        opacity: 0,
        skewX: 30,
      },
      visible: {
        opacity: 1,
        skewX: 0,
        transition: {
          duration,
          delay,
          ease: [0.6, -0.05, 0.01, 0.99],
        },
      },
    },

    "fade-scale": {
      hidden: {
        opacity: 0,
        scale: 1.2,
      },
      visible: {
        opacity: 1,
        scale: 1,
        transition: {
          duration,
          delay,
          ease: [0.3, 0, 0.3, 1],
        },
      },
    },

    "blur-in": {
      hidden: {
        opacity: 0,
        filter: "blur(10px)",
      },
      visible: {
        opacity: 1,
        filter: "blur(0px)",
        transition: {
          duration,
          delay,
          ease: "easeOut",
        },
      },
    },

    "drop-in": {
      hidden: {
        opacity: 0,
        y: -200,
        scale: 0.9,
      },
      visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
          type: "spring",
          duration,
          delay,
          bounce: 0.3,
        },
      },
    },

    "shake": {
      hidden: { opacity: 0, x: 0 },
      visible: {
        opacity: 1,
        x: [0, -10, 10, -10, 10, 0],
        transition: {
          duration: 0.6,
          delay,
          ease: "easeInOut",
        },
      },
    },

    "pulse": {
      hidden: { opacity: 0, scale: 0.95 },
      visible: {
        opacity: 1,
        scale: [1, 1.05, 1],
        transition: {
          duration,
          delay,
          ease: "easeInOut",
        },
      },
    },

    "tada": {
      hidden: { opacity: 0, scale: 0.9 },
      visible: {
        opacity: 1,
        scale: [1, 1.1, 0.9, 1.1, 1],
        rotate: [0, 3, -3, 3, 0],
        transition: {
          duration,
          delay,
          ease: "easeInOut",
        },
      },
    },
  };

  const MotionTag = motion[as] || motion.div;

  return (
    <MotionTag
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={animationVariants[animation] || animationVariants["fade-up"]}
      className={className}
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : undefined }}
    >
      {children}
    </MotionTag>
  );
}
